import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { UserContext } from "../../UserContext"; // ✅ Import User Context
import "../../index.css"; // Import the CSS file directly

const Nature = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = "nature"; // Hardcoded category for nature videos
  const [videos, setVideos] = useState([]); // State to hold videos
  const [loading, setLoading] = useState(true); // State to handle loading

  // ✅ Fetch videos when the component mounts
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error("Error fetching videos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful
  const handleHelpfulClick = async (videoId) => {
    if (!user || !user.id) {
      alert("You must be logged in to mark as helpful.");
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error("Error marking video as helpful:", error);
    }
  };

  return (
    <div className="page-container nature-page">
            <p class="disclaimer">
        Disclaimer: As an Amazon Associate, I earn from qualifying purchases. If you make a purchase through the links here, 
        I may earn a small commission at no extra cost to you. Thank you for your support.
        </p>
        <div className="micro-void"></div>
      <h1 className="page-title">Connect with Nature</h1>

      {/* Intro Section */}
      <img src="/images/wf-nature.png" alt="Person meditating" className="page-image" />
      <p className="page-text">
        Spending time in nature has a profound impact on both physical and mental health. It has been shown
        to reduce stress, improve mood, and even boost immunity. Whether it's a walk in the park or a weekend
        hike, immersing yourself in nature offers benefits that can enhance your overall well-being.
      </p>

      {/* Health Benefits Section */}
      <h2 className="page-heading">Health Benefits</h2>
      <p className="page-text">Connecting with nature has numerous health benefits. These include:</p>
      <ul className="content-list">
        <li>Reduced stress and anxiety</li>
        <li>Improved mental clarity and focus</li>
        <li>Enhanced mood and emotional well-being</li>
        <li>Strengthened immune system</li>
        <li>Increased physical activity and fitness</li>
        <li>Better sleep quality</li>
      </ul>

      <hr className="page-divider" />


      {/* Additional Content */}
      <h2 className="page-heading">Create a Habit Plan</h2>
      <p className="page-text">
        Building a habit of spending time in nature is an excellent way to improve both mental and physical
        health. Here's a simple framework to help you design your nature habit plan:
      </p>

      <h3 className="subheading">Framework for Building a Nature Habit:</h3>
      <ul className="content-list">
        <li>
          <strong>Start Small:</strong> Begin with simple activities like a 10-minute walk in the park or
          sitting outside for a few minutes to relax and breathe deeply.
        </li>
        <li>
          <strong>Make It Regular:</strong> Try to incorporate outdoor activities into your daily or weekly
          routine. You could aim for a 30-minute walk three times a week or a weekend hike.
        </li>
        <li>
          <strong>Track Your Progress:</strong> Use a journal, app, or simple checklist to track your outdoor
          activities and reflect on how you feel after each experience.
        </li>
      </ul>

      <h3 className="subheading">Examples of Nature Habits:</h3>
      <ul className="content-list">
        <li>Take a short walk outside every morning before work.</li>
        <li>Spend time in a park each weekend to unplug and relax.</li>
        <li>Go for a hike once a month to explore new trails and enjoy nature.</li>
      </ul>

      <p className="page-text">
        By establishing small, consistent outdoor practices, you can foster a lasting connection with nature
        and enjoy its many benefits.
      </p>

      <hr className="page-divider" />
      <h2 className="page-heading">Curated Video Collection</h2>

      {/* Video Section */}
      {loading ? (
        <p className="message">Loading videos...</p>
      ) : (
        videos.map((video) => (
          <div key={video._id} className="video-card">
            <h3 className="video-title">{video.title}</h3>
            <p className="video-details">
              <strong>Source:</strong> {video.source} <br />
              <strong>Length:</strong> {video.length} <br />
              {video.description}
            </p>
            <iframe
              className="video-frame"
              src={video.url}
              title={video.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>

            {/* ✅ Helpful Button */}
            <div className="video-actions">
              <button
                onClick={() => handleHelpfulClick(video._id)}
                disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                className={`button ${video.usersWhoClickedHelpful?.includes(user?.id) ? "disabled" : ""}`}
              >
                {video.usersWhoClickedHelpful?.includes(user?.id)
                  ? `Helpful (${video.helpfulCount})`
                  : `Mark as Helpful (${video.helpfulCount || 0})`}
              </button>
            </div>
          </div>
        ))
      )}

<hr className="section-divider" />
<h2 className="section-title">Curated Book Collection</h2>

<div className="book-card">
<h3 className="book-title">Braiding Sweetgrass</h3>
  
  {/* Book Cover Image from Open Library */}
  <img 
    src="https://covers.openlibrary.org/b/id/12836879-M.jpg" // Open Library cover image URL
    alt="Braiding Sweetgrass by Robin Wall Kimmerer" 
    className="book-cover" 
  />
  
  {/* Attribution for Image Source */}
  <p className="image-source">Image Source: Open Library API</p>
  
  <div className="micro-void"></div>
  
  <p className="book-details">
    <strong>Author:</strong> Robin Wall Kimmerer <br />
    <div className="micro-void"></div>
    <strong>Overview:</strong> Braiding Sweetgrass: <i>Indigenous Wisdom, Scientific Knowledge, and the Teachings of Plants</i> blends indigenous wisdom, scientific knowledge, and personal storytelling. Robin Wall Kimmerer, a botanist and member of the Citizen Potawatomi Nation, explores how we can reconnect with nature through respect, reciprocity, and gratitude. <br />
    <div className="barely-void"></div>
    <strong>Why It's Great:</strong> This beautiful book emphasizes the importance of nurturing our relationship with the earth. It provides invaluable lessons on environmental stewardship, healing, and honoring the Earth’s gifts through a mixture of scientific insight and indigenous teachings.
  </p>
  
  <div className="micro-void"></div>
  
  <a
    href="https://amzn.to/4iilwfr" // Your actual Amazon affiliate link
    className="amazon-link"
    target="_blank"
    rel="noopener noreferrer"
  >
    Buy on Amazon-https://amzn.to/4iilwfr
  </a>
  
  {/* Affiliate Link Disclosure */}
  <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
  <div className="short-line"></div>
</div>

<div className="book-card">
  <h3 className="book-title">The Overstory</h3>
  
  {/* Book Cover Image from Open Library */}
  <img 
    src="https://covers.openlibrary.org/b/id/8809047-M.jpg" // Open Library cover image URL
    alt="The Overstory by Richard Powers" 
    className="book-cover" 
  />
  
  {/* Attribution for Image Source */}
  <p className="image-source">Image Source: Open Library API</p>
  
  <div className="micro-void"></div>
  
  <p className="book-details">
    <strong>Author:</strong> Richard Powers <br />
    <div className="micro-void"></div>
    <strong>Overview:</strong> *The Overstory* intertwines the lives of multiple characters whose personal stories are connected by trees and the natural world. Richard Powers weaves a narrative that highlights the environmental challenges of our time and our often strained relationship with nature. <br />
    <div className="barely-void"></div>
    <strong>Why It's Great:</strong> This Pulitzer Prize-winning novel is a masterful exploration of the deep bonds between humans and trees. Powers’s writing is rich and immersive, making it a must-read for anyone passionate about the environment and human connection to nature.
  </p>
  
  <div className="micro-void"></div>
  
  <a
    href="https://amzn.to/4iv3uX0" // Your actual Amazon affiliate link
    className="amazon-link"
    target="_blank"
    rel="noopener noreferrer"
  >
    Buy on Amazon-https://amzn.to/4iv3uX0
  </a>
  
  {/* Affiliate Link Disclosure */}
  <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
  <div className="short-line"></div>
</div>

<div className="book-card">
  <h3 className="book-title">A Walk in the Woods</h3>
  
  {/* Book Cover Image from Open Library */}
  <img 
    src="https://covers.openlibrary.org/b/id/744722-M.jpg" // Open Library cover image URL
    alt="A Walk in the Woods by Bill Bryson" 
    className="book-cover" 
  />
  
  {/* Attribution for Image Source */}
  <p className="image-source">Image Source: Open Library API</p>
  
  <div className="micro-void"></div>
  
  <p className="book-details">
    <strong>Author:</strong> Bill Bryson <br />
    <div className="micro-void"></div>
    <strong>Overview:</strong> *A Walk in the Woods* chronicles Bill Bryson’s adventure hiking the Appalachian Trail. Along the way, he blends humor, history, and personal reflections while exploring the physical and mental challenges of trekking through the wilderness. <br />
    <div className="barely-void"></div>
    <strong>Why It's Great:</strong> A humorous and insightful journey, this book offers readers both entertainment and inspiration. Bryson’s witty narrative will leave you both laughing and thinking about the beauty and harshness of nature.
  </p>
  
  <div className="micro-void"></div>
  
  <a
    href="https://amzn.to/4bJinmw" // Your actual Amazon affiliate link
    className="amazon-link"
    target="_blank"
    rel="noopener noreferrer"
  >
    Buy on Amazon-https://amzn.to/4bJinmw
  </a>
  
  {/* Affiliate Link Disclosure */}
  <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
  <div className="short-line"></div>
</div>

<div className="book-card">
  <h3 className="book-title">Wild</h3>
  
  {/* Book Cover Image from Open Library */}
  <img 
    src="https://covers.openlibrary.org/b/id/7249596-L.jpg" // Open Library cover image URL
    alt="Wild by Cheryl Strayed" 
    className="book-cover" 
  />
  
  {/* Attribution for Image Source */}
  <p className="image-source">Image Source: Open Library API</p>
  
  <div className="micro-void"></div>
  
  <p className="book-details">
    <strong>Author:</strong> Cheryl Strayed <br />
    <div className="micro-void"></div>
    <strong>Overview:</strong> Wild is Cheryl Strayed’s memoir of hiking the Pacific Crest Trail, a journey of personal healing and self-discovery. After a series of personal tragedies, Strayed sets off on a transformative solo trek that challenges her physically, emotionally, and spiritually. <br />
    <div className="barely-void"></div>
    <strong>Why It's Great:</strong> This memoir is a powerful story of resilience and personal growth. Strayed’s writing is raw and emotional, making it a deeply inspirational read for anyone looking to overcome obstacles and reconnect with themselves.
  </p>
  
  <div className="micro-void"></div>
  
  <a
    href="https://amzn.to/4iX4Aev" // Your actual Amazon affiliate link
    className="amazon-link"
    target="_blank"
    rel="noopener noreferrer"
  >
    Buy on Amazon-https://amzn.to/4iX4Aev
  </a>
  
  {/* Affiliate Link Disclosure */}
  <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
  <div className="short-line"></div>
</div>



      <hr className="section-divider" />


      <h2 className="page-heading">Popular Apps for Nature Exploration</h2>
      <p className="page-text">
        Whether you're hiking, exploring new parks, or geocaching, these apps can help you make the most of
        your outdoor adventures:
      </p>

      <h3 className="subheading">Geocaching</h3>
      <p className="page-text">
        Geocaching is a global treasure hunt using GPS. It involves finding hidden "caches" placed by others,
        using clues and coordinates. This app makes it easy to find local caches and track your progress,
        adding excitement to outdoor activities.
      </p>

      <h3 className="subheading">AllTrails</h3>
      <p className="page-text">
        AllTrails is a popular app for discovering hiking trails, biking routes, and outdoor activities. It
        provides detailed maps, user reviews, and trail information to help you find the best routes for your
        outdoor adventures, from beginner to expert.
      </p>

      <hr />

      <div className="forum-section">
        <h2 className="page-heading">Join the Discussion</h2>
        <p className="page-text">
          Have questions or insights about connecting with nature? Join our forum to connect with others who
          are exploring this topic.
        </p>
        <div className="mini_void"></div>
        <a href="/forum/Nature" className="forum-link">
          Go to Nature Forum
        </a>
      </div>
    </div>
  );
};

export default Nature;
